/**
 * SuccessModal Component
 * Cool game-like celebration screen displayed after successfully creating a challenge
 */

import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Modal,
  Pressable,
  View,
  Text,
  Animated,
  StyleSheet,
  Platform,
  StatusBar,
  Dimensions,
  Easing,
  type ImageSourcePropType,
} from 'react-native';
import { Image } from 'expo-image';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useOverlayStore } from '../../../src/shared/stores/overlayStore';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useUserProfile } from '../../../src/shared/contexts/UserProfileContext';
import { getUserDisplayName } from '../../../src/friends/helpers/friendsUser.helper';
import type { Group } from '../../../types/challenges';
import type { Friend, PickSlip, Game } from '../../../src/create/types/create.types';
import { getCdnUrlExactKey, ASSET_PATHS, DEFAULT_GROUP_IMAGE_SOURCE } from '../../../constants/assets';
import { getEarliestKickoffFromPickSlips } from '../../../src/create/helpers/createGames.helper';
import { OPEN_CHALLENGE_PARTICIPANT_ID } from '../../../src/create/constants/create.constants';
import { OPEN_CHALLENGE_OPPONENT_LABEL } from '../../../src/challenges/constants/challenges.constants';
import { isAvatarUrl } from '../../../src/create/helpers/createUi.helper';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { getSuccessModalStyles } from './SuccessModal.styles';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';

const DESKTOP_BACKGROUND = {
  uri: getCdnUrlExactKey(ASSET_PATHS.CHALLENGE_TRANSITION_BACKGROUND_CPU),
};
const PHONE_BACKGROUND = {
  uri: getCdnUrlExactKey(ASSET_PATHS.CHALLENGE_TRANSITION_BACKGROUND_PHONE),
};

const SPARK_COUNT = 18;
const SPARK_SIZE_STYLES = ['sparkSizeEmber', 'sparkSizeFleck'] as const;
const SPARK_COLOR_STYLES = ['sparkColorGold', 'sparkColorAmber', 'sparkColorPurple', 'sparkColorWhite'] as const;

type SparkSizeStyleKey = (typeof SPARK_SIZE_STYLES)[number];
type SparkColorStyleKey = (typeof SPARK_COLOR_STYLES)[number];

type SparkDescriptor = {
  key: string;
  angle: number;
  delay: number;
  distance: number;
  duration: number;
  sizeStyle: SparkSizeStyleKey;
  colorStyle: SparkColorStyleKey;
};

const seededUnit = (index: number, salt: number): number => {
  const value = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const SPARKS: SparkDescriptor[] = Array.from({ length: SPARK_COUNT }, (_, index) => {
  const angle = seededUnit(index, 1) * 2 * Math.PI;
  return {
    key: `spark-${index}`,
    angle,
    delay: seededUnit(index, 3) * 900,
    distance: 90 + seededUnit(index, 4) * 220,
    duration: 520 + seededUnit(index, 5) * 520,
    sizeStyle: seededUnit(index, 6) > 0.62 ? SPARK_SIZE_STYLES[1] : SPARK_SIZE_STYLES[0],
    colorStyle: SPARK_COLOR_STYLES[Math.floor(seededUnit(index, 7) * SPARK_COLOR_STYLES.length)],
  };
});

// Web-specific fullscreen CSS (must be in component file for runtime injection)
const WEB_FULLSCREEN_CSS = `
  html, body {
    overflow: hidden !important;
    margin: 0 !important;
    padding: 0 !important;
    height: 100vh !important;
    position: fixed !important;
    width: 100% !important;
  }
`;

interface SuccessModalProps {
  visible: boolean;
  onClose: () => void;
  selectedGroupId: string | null;
  selectedGroup: Group | null;
  selectedFriendId: string | null;
  friends: Friend[];
  pickSlips: PickSlip[];
  games: Game[];
  totalPoints: number;
  /** Test ID for testing */
  testID?: string;
}

type CinematicSparkProps = {
  descriptor: SparkDescriptor;
  styles: ReturnType<typeof getSuccessModalStyles>;
  screenWidth: number;
  screenHeight: number;
};

const CinematicSpark: React.FC<CinematicSparkProps> = ({
  descriptor,
  styles,
  screenWidth,
  screenHeight,
}) => {
  const sparkAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.delay(descriptor.delay),
        Animated.timing(sparkAnim, {
          toValue: 0,
          duration: 0,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(sparkAnim, {
          toValue: 1,
          duration: descriptor.duration,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: Platform.OS !== 'web',
        }),
      ])
    );

    loop.start();
    return () => loop.stop();
  }, [descriptor.delay, descriptor.duration, sparkAnim]);

  const destX = Math.cos(descriptor.angle) * descriptor.distance;
  const destY = Math.sin(descriptor.angle) * descriptor.distance;
  const rotation = `${descriptor.angle * (180 / Math.PI)}deg`;

  const translateX = sparkAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, destX],
  });

  const translateY = sparkAnim.interpolate({
    inputRange: [0, 0.65, 1],
    outputRange: [0, destY * 0.9, destY + screenHeight * 0.07],
  });

  const opacity = sparkAnim.interpolate({
    inputRange: [0, 0.08, 0.64, 1],
    outputRange: [0, 1, 0.72, 0],
  });

  const scale = sparkAnim.interpolate({
    inputRange: [0, 0.2, 1],
    outputRange: [0.65, 1.15, 0.72],
  });

  return (
    <Animated.View
      style={[
        styles.sparkBase,
        styles[descriptor.sizeStyle],
        styles[descriptor.colorStyle],
        {
          opacity,
          transform: [
            { translateX: screenWidth / 2 },
            { translateY: screenHeight * 0.48 },
            { translateX },
            { translateY },
            { rotate: rotation },
            { scale },
          ],
        },
      ]}
    />
  );
};

export const SuccessModal: React.FC<SuccessModalProps> = ({
  visible,
  onClose,
  selectedGroupId,
  selectedGroup,
  selectedFriendId,
  friends,
  pickSlips,
  games,
  totalPoints: _totalPoints,
  testID,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
  // Narrow = horizontal fit (avatars + VS badge in one row). Typical phone width 320-430px.
  const isNarrowScreen = screenWidth < 480;
  const styles = useMemo(
    () =>
      getSuccessModalStyles(theme, {
        insets: {
          top: insets.top,
          bottom: insets.bottom,
          left: insets.left,
          right: insets.right,
        },
        isNarrowScreen,
      }),
    [theme, insets.top, insets.bottom, insets.left, insets.right, isNarrowScreen]
  );
  const { user } = useSupabaseAuth();
  const { profile } = useUserProfile();

  const backgroundSource = useMemo<ImageSourcePropType>(
    () => (isNarrowScreen ? PHONE_BACKGROUND : DESKTOP_BACKGROUND),
    [isNarrowScreen]
  );
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  const userName = getUserDisplayName(user) || 'You';
  // "Post to anyone" rides a reserved participant id with no matching friend — show the default
  // group cover for the not-yet-known taker rather than initials (specs/open-challenges).
  const isOpenRival = selectedFriendId === OPEN_CHALLENGE_PARTICIPANT_ID;
  const selectedFriend =
    selectedFriendId && !isOpenRival ? friends.find((f) => f.id === selectedFriendId) : null;
  const friendName =
    selectedGroupId && selectedGroup
      ? selectedGroup.name
      : isOpenRival
        ? OPEN_CHALLENGE_OPPONENT_LABEL
        : selectedFriend?.name || 'Friend';

  const gameCount = pickSlips.length;

  useEffect(() => {
    if (visible) {
      // Calculate initial countdown using same kickoff parsing as rest of app
      const earliestKickoff = getEarliestKickoffFromPickSlips(pickSlips, games);
      if (earliestKickoff) {
        const now = Date.now();
        const diff = Math.max(0, Math.floor((earliestKickoff.getTime() - now) / 1000));
        setCountdownSeconds(diff);
      } else {
        setCountdownSeconds(3);
      }

      const timer = setInterval(() => {
        const earliestKickoff = getEarliestKickoffFromPickSlips(pickSlips, games);
        if (earliestKickoff) {
          const now = Date.now();
          const diff = Math.max(0, Math.floor((earliestKickoff.getTime() - now) / 1000));
          setCountdownSeconds(diff);
        } else {
          setCountdownSeconds(0);
        }
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [visible, pickSlips, games]);

  // This modal covers the app root, where the "+X XP" flash and level-up / rank-up
  // ceremonies render. Creating a challenge spends stake coins, which refetches XP and
  // fires that feedback right as we open — so hold it until we close (see overlayStore).
  useEffect(() => {
    if (!visible) return;
    const { pushBlockingOverlay, popBlockingOverlay } = useOverlayStore.getState();
    pushBlockingOverlay();
    return () => popBlockingOverlay();
  }, [visible]);

  // Hide status bar and navigation bar when modal is visible
  useEffect(() => {
    if (visible) {
      StatusBar.setHidden(true, 'fade');

      // Hide browser UI on web
      if (Platform.OS === 'web' && typeof document !== 'undefined') {
        const style = document.createElement('style');
        style.id = 'success-modal-fullscreen';
        style.textContent = WEB_FULLSCREEN_CSS;
        document.head.appendChild(style);

        return () => {
          const styleEl = document.getElementById('success-modal-fullscreen');
          if (styleEl && document.head.contains(styleEl)) {
            document.head.removeChild(styleEl);
          }
          StatusBar.setHidden(false, 'fade');
        };
      }

      return () => {
        StatusBar.setHidden(false, 'fade');
      };
    }
  }, [visible]);

  // Small = short viewport (smaller avatars). Narrow = narrow viewport (row fits width). Use compact row when either.
  const isSmallScreen = screenHeight < 700 && screenWidth < 450;
  const useCompactRow = isNarrowScreen || isSmallScreen;
  const avatarSize = useCompactRow ? 118 : Platform.OS === 'web' ? 210 : 168;

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="none"
      statusBarTranslucent={true}
      onRequestClose={onClose}
      testID={testID || 'success-modal'}
    >
      <View style={styles.successModalContainer}>
        <Image
          source={backgroundSource}
          style={styles.backgroundImage}
          contentFit="cover"
          alt={WEB_IMAGE_ALT_DECORATIVE}
        />
        <View style={styles.backgroundScrim} pointerEvents="none" />

        {/* Backdrop - only this area should dismiss the modal */}
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          testID="success-modal-dismiss"
        />

        <View style={styles.fxLayer} pointerEvents="none">
          <View style={styles.centerBlast} />
          <View style={styles.sparkLayer}>
            {SPARKS.map((spark) => (
              <CinematicSpark
                key={spark.key}
                descriptor={spark}
                styles={styles}
                screenWidth={screenWidth}
                screenHeight={screenHeight}
              />
            ))}
          </View>
        </View>

        {/* Content wrapper absorbs taps so the backdrop's onPress doesn't dismiss the modal. */}
        <Pressable
          onPress={() => {
            void 0
          }}
          style={styles.successModalContentTouchable}
        >
          <View
            style={[
              styles.successModalContentSurface,
              isSmallScreen && styles.successModalContentSurfaceSmall,
            ]}
          >
            <View style={styles.successModalContentWrapper}>
              <View
                style={[
                  styles.successBanner,
                  isSmallScreen && styles.successBannerSmall,
                ]}
              >
                <Text style={[styles.eyebrowText, isSmallScreen && styles.eyebrowTextSmall]}>
                  Challenge Sent
                </Text>
                <Text style={[styles.successBannerTitle, isSmallScreen && styles.successBannerTitleSmall]}>
                  READY TO
                </Text>
                <Text style={[styles.successBannerTitle, styles.successBannerTitleAccent, isSmallScreen && styles.successBannerTitleSmall]}>
                  RUMBLE!
                </Text>
                <Text style={[styles.successBannerSubtitle, isSmallScreen && styles.successBannerSubtitleSmall]}>
                  {gameCount} game{gameCount !== 1 ? 's' : ''} locked in against
                </Text>
                <Text style={[styles.successBannerName, isSmallScreen && styles.successBannerNameSmall]}>
                  {friendName}
                </Text>
              </View>

              <View
                style={[
                  styles.charactersContainer,
                  isSmallScreen && styles.charactersContainerSmall,
                  isNarrowScreen && styles.charactersContainerNarrow,
                ]}
              >
                {/* YOU (challenger) - current user avatar, home image */}
                <View
                  style={[
                    styles.characterWrapper,
                    useCompactRow && styles.characterWrapperSmall,
                  ]}
                >
                  <FriendAvatar
                    avatarId={(profile?.avatarId ?? undefined) as DisplayAvatarId | undefined}
                    avatarConfig={profile?.avatarConfig ?? null}
                    size={avatarSize}
                    nameForInitials={userName}
                    style={StyleSheet.flatten([
                      styles.successModalAvatar,
                      useCompactRow && styles.successModalAvatarSmall,
                    ])}
                  />
                  <Text style={[styles.characterLabel, useCompactRow && styles.characterLabelSmall]}>
                    YOU
                  </Text>
                  <Text style={[styles.characterName, useCompactRow && styles.characterNameSmall]}>
                    {userName}
                  </Text>
                </View>

                <View
                  style={[
                    styles.vsBadge,
                    useCompactRow && styles.vsBadgeSmall,
                  ]}
                >
                  <Text style={[styles.vsText, useCompactRow && styles.vsTextSmall]}>VS</Text>
                </View>

                <View
                  style={[
                    styles.characterWrapper,
                    useCompactRow && styles.characterWrapperSmall,
                  ]}
                >
                  {selectedGroupId || isOpenRival ? (
                    <FriendAvatar
                      imageSource={DEFAULT_GROUP_IMAGE_SOURCE}
                      size={avatarSize}
                      nameForInitials={
                        selectedGroupId ? selectedGroup?.name ?? 'Group' : OPEN_CHALLENGE_OPPONENT_LABEL
                      }
                      style={StyleSheet.flatten([
                        styles.successModalAvatar,
                        useCompactRow && styles.successModalAvatarSmall,
                        styles.successModalAvatarRight,
                      ])}
                    />
                  ) : (
                    <FriendAvatar
                      avatarId={
                        selectedFriend
                          ? ((selectedFriend as { avatarId?: DisplayAvatarId; avatar?: string }).avatarId ??
                            (!isAvatarUrl(selectedFriend.avatar)
                              ? (selectedFriend.avatar as DisplayAvatarId)
                              : undefined))
                          : undefined
                      }
                      avatarConfig={
                        (selectedFriend as { avatarConfig?: EquippedMap | null } | null)?.avatarConfig
                      }
                      fallbackUrl={
                        selectedFriend && isAvatarUrl(selectedFriend.avatar)
                          ? selectedFriend.avatar
                          : undefined
                      }
                      size={avatarSize}
                      nameForInitials={friendName}
                      style={StyleSheet.flatten([
                        styles.successModalAvatar,
                        useCompactRow && styles.successModalAvatarSmall,
                        styles.successModalAvatarRight,
                      ])}
                    />
                  )}
                  <Text style={[styles.characterLabel, styles.characterLabelAway, useCompactRow && styles.characterLabelSmall]}>
                    RIVAL
                  </Text>
                  <Text
                    style={[
                      styles.characterName,
                      styles.characterNameFriend,
                      useCompactRow && styles.characterNameSmall,
                    ]}
                  >
                    {friendName}
                  </Text>
                </View>
              </View>

              <View style={[styles.countdownContainer, isSmallScreen && styles.countdownContainerSmall]}>
                <Text style={[styles.countdownLabel, isSmallScreen && styles.countdownLabelSmall]}>
                  Kickoff countdown
                </Text>
                <View style={[styles.countdownTimerBox, isSmallScreen && styles.countdownTimerBoxSmall]}>
                  <Text style={[styles.countdownTimer, isSmallScreen && styles.countdownTimerSmall]}>
                    {String(Math.floor(countdownSeconds / 3600)).padStart(2, '0')}:
                    {String(Math.floor((countdownSeconds % 3600) / 60)).padStart(2, '0')}:
                    {String(countdownSeconds % 60).padStart(2, '0')}
                  </Text>
                </View>
                <Text style={[styles.countdownHint, isSmallScreen && styles.countdownHintSmall]}>
                  Until first match starts
                </Text>
              </View>
            </View>
          </View>
        </Pressable>
      </View>
    </Modal>
  );
};
